import { useEffect, useRef } from 'react';
import {
  alpha,
  Box,
  Checkbox,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import FolderIcon from '@mui/icons-material/Folder';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import StarIcon from '@mui/icons-material/Star';
import { DEFAULT_APPEARANCE, GRID_SIZE_OPTIONS } from '../utils/constants';
import { getFileDisplayName } from '../utils/workspace';
import ItemActionsMenu from './ItemActionsMenu';

const LONG_PRESS_MS = 380;

function ItemIcon({ type, color }) {
  return type === 'folder' ? (
    <FolderIcon sx={{ fontSize: 38, color: color || 'warning.main' }} />
  ) : (
    <InsertDriveFileIcon sx={{ fontSize: 36 }} />
  );
}

function getSelectionLabel(entry) {
  return entry.type === 'file' ? getFileDisplayName(entry.item) : entry.item.name;
}

function useTouchSelection(entry, selectionMode, onLongPress) {
  const timerRef = useRef(null);
  const ignoreClickRef = useRef(false);

  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);

  const clearTimer = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  const handlePointerDown = (event) => {
    if (selectionMode || event.pointerType !== 'touch' || !onLongPress) {
      return;
    }

    clearTimer();
    timerRef.current = window.setTimeout(() => {
      ignoreClickRef.current = true;
      onLongPress(entry);
      timerRef.current = null;
    }, LONG_PRESS_MS);
  };

  const handleClickCapture = (event) => {
    if (!ignoreClickRef.current) {
      return false;
    }

    ignoreClickRef.current = false;
    event.preventDefault();
    event.stopPropagation();
    return true;
  };

  return {
    handlePointerDown,
    clearTimer,
    handleClickCapture,
  };
}

function ExplorerCard({
  entry,
  onActivate,
  onLongPress,
  onToggleSelection,
  menuProps,
  selected,
  selectionMode,
}) {
  const theme = useTheme();
  const isFile = entry.type === 'file';
  const item = entry.item;
  const appearance = { ...DEFAULT_APPEARANCE, ...(item.appearance || {}) };
  const cardColor = isFile ? appearance.textColor : 'text.primary';
  const backgroundColor = isFile ? appearance.backgroundColor : 'background.paper';
  const borderColor = selected
    ? theme.palette.primary.main
    : isFile
      ? alpha(appearance.textColor, theme.palette.mode === 'dark' ? 0.32 : 0.16)
      : 'divider';
  const secondaryText =
    entry.subtitle ||
    (isFile ? `${(item.content || '').length.toLocaleString()} characters` : 'Folder');
  const selectionLabel = getSelectionLabel(entry);
  const touchSelection = useTouchSelection(entry, selectionMode, onLongPress);

  return (
    <Paper
      role="button"
      tabIndex={0}
      onClickCapture={touchSelection.handleClickCapture}
      onPointerDown={touchSelection.handlePointerDown}
      onPointerUp={touchSelection.clearTimer}
      onPointerCancel={touchSelection.clearTimer}
      onPointerLeave={touchSelection.clearTimer}
      onClick={(event) =>
        onActivate(entry, {
          shiftKey: event.shiftKey,
          metaKey: event.metaKey,
          ctrlKey: event.ctrlKey,
        })
      }
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onActivate(entry, {
            shiftKey: event.shiftKey,
            metaKey: event.metaKey,
            ctrlKey: event.ctrlKey,
          });
        }
      }}
      sx={{
        position: 'relative',
        minHeight: 142,
        borderRadius: 1,
        border: 1,
        borderColor,
        bgcolor: backgroundColor,
        color: cardColor,
        overflow: 'hidden',
        cursor: 'pointer',
        userSelect: 'none',
        boxShadow: selected ? 4 : 0,
        transition: 'transform 160ms ease, border-color 160ms ease, box-shadow 160ms ease',
        '&:hover': {
          transform: selectionMode ? 'none' : 'translateY(-2px)',
          boxShadow: selected ? 5 : 4,
        },
        '&:focus-visible': {
          outline: `2px solid ${theme.palette.primary.main}`,
          outlineOffset: 2,
        },
      }}
    >
      {entry.type === 'folder' && item.tagColor ? (
        <Box sx={{ height: 5, bgcolor: item.tagColor }} />
      ) : null}
      {selectionMode ? (
        <Checkbox
          checked={selected}
          onClick={(event) => event.stopPropagation()}
          onChange={() => onToggleSelection(entry)}
          slotProps={{ input: { 'aria-label': `Select ${selectionLabel}` } }}
          sx={{
            position: 'absolute',
            top: entry.type === 'folder' && item.tagColor ? 8 : 6,
            left: 6,
            zIndex: 2,
            bgcolor: alpha(theme.palette.background.paper, 0.92),
            borderRadius: '50%',
          }}
        />
      ) : null}
      <Box
        sx={{
          p: 2,
          minHeight: entry.type === 'folder' && item.tagColor ? 137 : 142,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Stack
          direction="row"
          sx={{ alignItems: 'flex-start', justifyContent: 'space-between' }}
        >
          <ItemIcon type={entry.type} color={item.tagColor} />
          <ItemActionsMenu
            {...menuProps}
            type={entry.type}
            item={item}
            iconColor="inherit"
          />
        </Stack>

        <Box sx={{ mt: 'auto', minWidth: 0 }}>
          <Stack direction="row" spacing={0.75} sx={{ alignItems: 'center', minWidth: 0 }}>
            <Typography variant="subtitle1" fontWeight={800} noWrap>
              {selectionLabel}
            </Typography>
            {item.isFavorite ? <StarIcon fontSize="small" color="warning" /> : null}
          </Stack>
          <Typography
            variant="caption"
            sx={{ color: isFile ? alpha(appearance.textColor, 0.72) : 'text.secondary' }}
            noWrap
          >
            {secondaryText}
          </Typography>
        </Box>
      </Box>
    </Paper>
  );
}

function ExplorerListRow({
  entry,
  onActivate,
  onLongPress,
  onToggleSelection,
  menuProps,
  selected,
  selectionMode,
}) {
  const theme = useTheme();
  const item = entry.item;
  const isFile = entry.type === 'file';
  const appearance = { ...DEFAULT_APPEARANCE, ...(item.appearance || {}) };
  const secondaryText =
    entry.subtitle ||
    (isFile ? `${(item.content || '').length.toLocaleString()} characters` : 'Folder');
  const selectionLabel = getSelectionLabel(entry);
  const touchSelection = useTouchSelection(entry, selectionMode, onLongPress);

  return (
    <ListItem
      disablePadding
      secondaryAction={
        <ItemActionsMenu {...menuProps} type={entry.type} item={item} iconColor="inherit" />
      }
      sx={{
        mb: 0.75,
        border: 1,
        borderColor: selected ? 'primary.main' : 'divider',
        borderRadius: 1,
        overflow: 'hidden',
        bgcolor: selected ? 'action.selected' : 'background.paper',
        userSelect: 'none',
      }}
    >
      <ListItemButton
        selected={selected}
        onClickCapture={touchSelection.handleClickCapture}
        onPointerDown={touchSelection.handlePointerDown}
        onPointerUp={touchSelection.clearTimer}
        onPointerCancel={touchSelection.clearTimer}
        onPointerLeave={touchSelection.clearTimer}
        onClick={(event) =>
          onActivate(entry, {
            shiftKey: event.shiftKey,
            metaKey: event.metaKey,
            ctrlKey: event.ctrlKey,
          })
        }
        sx={{
          pr: 7,
          '&.Mui-selected': {
            bgcolor: alpha(theme.palette.primary.main, 0.08),
          },
          '&.Mui-selected:hover': {
            bgcolor: alpha(theme.palette.primary.main, 0.12),
          },
        }}
      >
        <ListItemIcon sx={{ minWidth: selectionMode ? 78 : 42 }}>
          <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
            {selectionMode ? (
              <Checkbox
                checked={selected}
                onClick={(event) => event.stopPropagation()}
                onChange={() => onToggleSelection(entry)}
                slotProps={{ input: { 'aria-label': `Select ${selectionLabel}` } }}
              />
            ) : null}
            {isFile ? (
              <Box
                sx={{
                  width: 30,
                  height: 36,
                  borderRadius: 0.75,
                  bgcolor: appearance.backgroundColor,
                  border: 1,
                  borderColor: 'divider',
                  display: 'grid',
                  placeItems: 'center',
                  color: appearance.textColor,
                }}
              >
                <InsertDriveFileIcon fontSize="small" />
              </Box>
            ) : (
              <FolderIcon sx={{ color: item.tagColor || 'warning.main' }} />
            )}
          </Stack>
        </ListItemIcon>
        <ListItemText
          primary={
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center', minWidth: 0 }}>
              <Typography variant="body1" fontWeight={700} noWrap>
                {selectionLabel}
              </Typography>
              {item.isFavorite ? <StarIcon fontSize="small" color="warning" /> : null}
            </Stack>
          }
          secondary={secondaryText}
          primaryTypographyProps={{ component: 'div' }}
        />
      </ListItemButton>
    </ListItem>
  );
}

export default function ExplorerItems({
  entries,
  viewMode,
  gridSize,
  onOpen,
  getMenuProps,
  selectionMode = false,
  selectedKeySet = new Set(),
  onEntryLongPress,
  onToggleSelection,
}) {
  const handleActivate = (entry, modifiers) => {
    onOpen(entry, modifiers, entries);
  };

  if (viewMode === 'list') {
    return (
      <List disablePadding>
        {entries.map((entry) => {
          const entryKey = `${entry.type}:${entry.item.id}`;

          return (
            <ExplorerListRow
              key={entryKey}
              entry={entry}
              onActivate={handleActivate}
              onLongPress={onEntryLongPress}
              onToggleSelection={onToggleSelection}
              menuProps={getMenuProps(entry)}
              selected={selectedKeySet.has(entryKey)}
              selectionMode={selectionMode}
            />
          );
        })}
      </List>
    );
  }

  const size = GRID_SIZE_OPTIONS.find((option) => option.value === gridSize) || GRID_SIZE_OPTIONS[2];

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: `repeat(auto-fill, minmax(min(100%, ${size.minWidth}px), 1fr))`,
        gap: 2,
      }}
    >
      {entries.map((entry) => {
        const entryKey = `${entry.type}:${entry.item.id}`;

        return (
          <ExplorerCard
            key={entryKey}
            entry={entry}
            onActivate={handleActivate}
            onLongPress={onEntryLongPress}
            onToggleSelection={onToggleSelection}
            menuProps={getMenuProps(entry)}
            selected={selectedKeySet.has(entryKey)}
            selectionMode={selectionMode}
          />
        );
      })}
    </Box>
  );
}
